// src\components\Super Admin\Lead Management\NotificationManagement.tsx

import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { getApiError, notificationApi } from "./notificationApi";
import type {
  BusinessEvent,
  Campaign,
  CampaignConfig,
  Channel,
  NamedGroup,
  NotificationLog,
  NotificationRule,
  NotificationTemplate,
  PageKey,
  Priority,
  QueueMetrics,
  RecipientType,
  ScheduleType,
  TemplateGroup,
  VariableDefinition,
  VariableResponse,
} from "./types";
import "./NotificationManagement.css";

// ✅ REMOVED: recipientGroups and templateGroups from menu
const menu: Array<[PageKey, string]> = [
  ["dashboard", "Dashboard"],
  ["campaigns", "Campaigns"],
  ["events", "Business Events"],
  ["rules", "Notification Rules"],
  ["templates", "Templates"],
  ["logs", "Notification Logs"],
  ["queues", "Queue Monitoring"],
];

const getError = getApiError;
const date = (value?: string) =>
  value ? new Date(value).toLocaleString() : "-";
const idOf = (value: { _id?: string; templateId?: string }) =>
  value._id || value.templateId || "";

// ──────────────────────────────────────────────────────────────
// Variable rendering helper
//
// VariableMapper builds `variables` as a flat map of dotted string
// keys, EXCEPT for a small set of top-level objects (actor, entity,
// metadata, request, response) that are set as real nested objects.
//
// Handlebars resolves nested objects with dotted paths —
// {{response.user.fullName}} — but flat keys must be addressed
// with bracket syntax — {{[audit.responsePayload.date]}} — because
// the intermediate objects don't exist.
//
// This helper is the single source of truth for that rule; both the
// "insert variable" button and the VariableChip use it.
// ──────────────────────────────────────────────────────────────
const NESTED_ROOTS = new Set([
  "actor",
  "entity",
  "metadata",
  "request",
  "response",
]);

const wrapVariable = (name: string): string => {
  if (!name.includes(".")) {
    return `{{${name}}}`;
  }
  const root = name.split(".")[0];
  return NESTED_ROOTS.has(root) ? `{{${name}}}` : `{{[${name}]}}`;
};

function useRemote<T>(
  loader: () => Promise<T>,
  deps: React.DependencyList = [],
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await loader());
    } catch (e) {
      setError(getError(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, loading, error, reload };
}

const PageState = ({
  loading,
  error,
  empty,
  children,
}: {
  loading: boolean;
  error: string;
  empty?: boolean;
  children: React.ReactNode;
}) => {
  if (loading) return <div className="nm-state">Loading…</div>;
  if (error) return <div className="nm-state nm-error">{error}</div>;
  if (empty) return <div className="nm-state">No records found.</div>;
  return <>{children}</>;
};

const Toolbar = ({
  title,
  search,
  setSearch,
  reload,
  action,
}: {
  title: string;
  search?: string;
  setSearch?: (v: string) => void;
  reload: () => void;
  action?: React.ReactNode;
}) => (
  <div className="nm-toolbar">
    <h2>{title}</h2>
    <div className="nm-toolbar-actions">
      {setSearch && (
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${title.toLowerCase()}…`}
        />
      )}
      {action}
      <button className="nm-secondary" onClick={reload}>
        Refresh
      </button>
    </div>
  </div>
);

function Dashboard() {
  const remote = useRemote(async () => {
    const [events, templates, rules, logs] = await Promise.all([
      notificationApi.events.list(),
      notificationApi.templates.list(),
      notificationApi.rules.list(),
      notificationApi.logs.recent(),
    ]);
    return { events, templates, rules, logs };
  }, []);
  const d = remote.data;
  return (
    <>
      <Toolbar title="Dashboard" reload={remote.reload} />
      <PageState loading={remote.loading} error={remote.error}>
        {d && (
          <>
            <div className="nm-cards">
              {[
                ["Business Events", d.events.length],
                ["Templates", d.templates.length],
                ["Rules", d.rules.length],
                ["Recent Notifications", d.logs.length],
              ].map(([label, value]) => (
                <div className="nm-card" key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            <LogTable logs={d.logs.slice(0, 10)} />
          </>
        )}
      </PageState>
    </>
  );
}

// ============================================================
// BUSINESS EVENTS PAGE - WITH DROPDOWNS
// ============================================================

function EventsPage() {
  const remote = useRemote(notificationApi.events.list, []);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<BusinessEvent | null>(null);
  const [open, setOpen] = useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // ✅ State for dropdown options
  const [services, setServices] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);

  const blank = {
    code: "",
    name: "",
    service: "",
    module: "",
    action: "",
    status: "SUCCESS",
  };
  const [form, setForm] = useState(blank);

  // ✅ Load dropdown options when modal opens
  useEffect(() => {
    if (open) {
      loadDropdownOptions();
    }
  }, [open]);

  const loadDropdownOptions = async () => {
    setLoadingDropdowns(true);
    try {
      // Load services
      const servicesRes = await notificationApi.discovery.services();
      setServices(servicesRes || []);

      // Load statuses
      const statusesRes = await notificationApi.discovery.statuses();
      setStatuses(statusesRes || []);

      // If editing, load actions for the selected service
      if (editing && form.service) {
        const actionsRes = await notificationApi.discovery.actions(
          form.service,
        );
        setActions(actionsRes || []);
      } else {
        setActions([]);
      }
    } catch (error) {
      console.error("Failed to load dropdown options:", error);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // ✅ Load actions when service changes
  const handleServiceChange = async (service: string) => {
    setForm({ ...form, service, action: "" });
    if (service) {
      try {
        const actionsRes = await notificationApi.discovery.actions(service);
        setActions(actionsRes || []);
      } catch (error) {
        console.error("Failed to load actions:", error);
        setActions([]);
      }
    } else {
      setActions([]);
    }
  };

  const rows = (remote.data || []).filter((x) =>
    JSON.stringify(x).toLowerCase().includes(search.toLowerCase()),
  );

  const edit = (x: BusinessEvent) => {
    setEditing(x);
    setForm({
      code: x.code,
      name: x.name,
      service: x.match.service,
      module: x.match.module || "",
      action: x.match.action,
      status: x.match.status || "",
    });
    // Load actions for the selected service
    if (x.match.service) {
      notificationApi.discovery.actions(x.match.service).then((res) => {
        setActions(res || []);
      });
    }
    setOpen(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = {
      code: form.code,
      name: form.name,
      enabled: editing?.enabled ?? true,
      match: {
        service: form.service,
        module: form.module,
        action: form.action,
        status: form.status,
      },
    };
    try {
      if (editing) {
        await notificationApi.events.update(editing._id, body);
      } else {
        await notificationApi.events.create(body);
      }
      setOpen(false);
      setEditing(null);
      setForm(blank);
      await remote.reload();
    } catch (error) {
      alert("Failed to save business event: " + getError(error));
    }
  };

  return (
    <>
      <Toolbar
        title="Business Events"
        search={search}
        setSearch={setSearch}
        reload={remote.reload}
        action={
          <button
            onClick={() => {
              setEditing(null);
              setForm(blank);
              setActions([]);
              setOpen(true);
            }}
          >
            + Event
          </button>
        }
      />
      <PageState
        loading={remote.loading}
        error={remote.error}
        empty={!rows.length}
      >
        <div className="nm-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Service</th>
                <th>Module</th>
                <th>Action</th>
                <th>Status</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x._id}>
                  <td>
                    <code>{x.code}</code>
                  </td>
                  <td>{x.name}</td>
                  <td>{x.match.service}</td>
                  <td>{x.match.module || "-"}</td>
                  <td>{x.match.action}</td>
                  <td>{x.match.status || "-"}</td>
                  <td>
                    <Status value={x.enabled ? "Enabled" : "Disabled"} />
                  </td>
                  <td className="nm-actions">
                    <button onClick={() => edit(x)}>Edit</button>
                    <button
                      className="nm-secondary"
                      onClick={async () => {
                        await notificationApi.events.toggle(x._id);
                        remote.reload();
                      }}
                    >
                      {x.enabled ? "Disable" : "Enable"}
                    </button>
                    <button
                      className="nm-danger"
                      onClick={async () => {
                        if (window.confirm("Delete this event?")) {
                          await notificationApi.events.remove(x._id);
                          remote.reload();
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageState>
      {open && (
        <Modal
          title={editing ? "Edit Business Event" : "New Business Event"}
          close={() => setOpen(false)}
        >
          {loadingDropdowns ? (
            <div className="nm-state">Loading options...</div>
          ) : (
            <form className="nm-form" onSubmit={submit}>
              <label>
                <span>Code *</span>
                <input
                  required
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g., USER_LOGIN_SUCCESS"
                />
              </label>
              <label>
                <span>Name *</span>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., User Login Success"
                />
              </label>
              <label>
                <span>Service *</span>
                <select
                  required
                  value={form.service}
                  onChange={(e) => handleServiceChange(e.target.value)}
                >
                  <option value="">Select Service</option>
                  {services.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Module (optional)</span>
                <input
                  value={form.module}
                  onChange={(e) => setForm({ ...form, module: e.target.value })}
                  placeholder="e.g., child-management"
                />
              </label>
              <label>
                <span>Action *</span>
                <select
                  required
                  value={form.action}
                  onChange={(e) => setForm({ ...form, action: e.target.value })}
                  disabled={!form.service}
                >
                  <option value="">
                    {form.service ? "Select Action" : "Select Service First"}
                  </option>
                  {actions.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Status</span>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="">Select Status</option>
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="nm-full">
                Save Event
              </button>
            </form>
          )}
        </Modal>
      )}
    </>
  );
}

// ============================================================
// TEMPLATES PAGE
// ============================================================

const ROUTE_OPTIONS = [
  { value: "home", label: "🏠 Home" },
  { value: "appointment_details", label: "📋 Appointment Details" },
  { value: "appointments", label: "📅 Appointments List" },
  { value: "reschedule", label: "🔄 Reschedule Appointment" },
  { value: "child_dashboard", label: "👶 Child Dashboard" },
  { value: "payment_history", label: "💳 Payment History" },
  { value: "reports", label: "📊 Reports" },
  { value: "rewards", label: "🎖️ Rewards" },
  { value: "health_heroes", label: "🦸 Health Heroes" },
  { value: "screening_tracker", label: "📋 Screening Tracker" },
  { value: "notifications", label: "🔔 Notifications" },
  { value: "contact_support", label: "📞 Contact Support" },
  { value: "faq", label: "❓ FAQ" },
  { value: "qr_code", label: "📱 QR Code" },
  { value: "make_payment", label: "💳 Make Payment" },
  { value: "schedule_screening", label: "📅 Schedule Screening" },
  { value: "chat", label: "💬 Chat" },
  { value: "add_child", label: "👶 Add Child" },
];

function TemplatesPage() {
  const remote = useRemote(notificationApi.templates.list, []);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [businessEvents, setBusinessEvents] = useState<BusinessEvent[]>([]);
  const [variables, setVariables] = useState<VariableResponse | null>(null);
  const [loadingVars, setLoadingVars] = useState(false);
  const [varError, setVarError] = useState<string | null>(null);
  const [showAllVariables, setShowAllVariables] = useState(false); // ✅ ADDED

  const blank: NotificationTemplate = {
    templateId: "",
    name: "",
    channel: "EMAIL" as Channel,
    subject: "",
    title: "",
    body: "",
    variables: [],
    route: "",
    action: "",
  };
  const [form, setForm] = useState(blank);
  const rows = (remote.data || []).filter((x) =>
    JSON.stringify(x).toLowerCase().includes(search.toLowerCase()),
  );

  const extractVariablesFromBody = (body: string): string[] => {
    const matches = body.match(/{{(.*?)}}/g) || [];
    const result: string[] = [];
    const seen = new Set<string>();

    for (const match of matches) {
      const variable = match.replace("{{", "").replace("}}", "").trim();
      if (!seen.has(variable)) {
        seen.add(variable);
        result.push(variable);
      }
    }
    return result;
  };

  // Load business events for variable dropdown
  useEffect(() => {
    if (open) {
      notificationApi.events.list().then((res) => setBusinessEvents(res));
    }
  }, [open]);

  // ✅ UPDATED: loadVariables now accepts showAll parameter
  const loadVariables = async (eventCode: string, showAll: boolean = false) => {
    if (!eventCode) {
      setVariables(null);
      setVarError(null);
      return;
    }
    setLoadingVars(true);
    setVarError(null);
    try {
      const result = await notificationApi.templates.variables(
        eventCode,
        showAll,
      );
      if (result && result.variables && Array.isArray(result.variables)) {
        setVariables(result);
        setShowAllVariables(showAll); // ✅ Sync the toggle state
      } else {
        setVarError("No variables found for this event");
        setVariables(null);
      }
    } catch (e) {
      console.error("Failed to load variables:", e);
      setVarError(e instanceof Error ? e.message : "Failed to load variables");
      setVariables(null);
    } finally {
      setLoadingVars(false);
    }
  };

  const insertVariable = (varName: string) => {
    const textarea = document.getElementById(
      "templateBody",
    ) as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    textarea.value =
      value.substring(0, start) +
      wrapVariable(varName) + // ← changed from `{{${varName}}}`
      value.substring(end);
    updatePreview();
  };
  // const insertVariable = (varName: string) => {
  //   const textarea = document.getElementById(
  //     "templateBody",
  //   ) as HTMLTextAreaElement;
  //   if (!textarea) return;
  //   const start = textarea.selectionStart;
  //   const end = textarea.selectionEnd;
  //   const value = textarea.value;
  //   textarea.value =
  //     value.substring(0, start) + `{{${varName}}}` + value.substring(end);
  //   updatePreview();
  // };

  const updatePreview = () => {
    const body = document.getElementById("templateBody") as HTMLTextAreaElement;
    const preview = document.getElementById("templatePreview");
    if (!body || !preview) return;
    let text = body.value;
    const samples: Record<string, string> = {
      "{{actor.name}}": "John Doe",
      "{{actor.email}}": "john@example.com",
      "{{actor.userId}}": "USR-1001",
      "{{metadata.location}}": "Bangalore, India",
      "{{metadata.ipAddress}}": "192.168.1.1",
      "{{entity.id}}": "ENT-123",
      "{{response.status}}": "SUCCESS",
      "{{response.message}}": "Operation completed successfully",
    };
    for (const [key, value] of Object.entries(samples)) {
      text = text.replace(new RegExp(key.replace(/[{}]/g, "\\$&"), "g"), value);
    }
    preview.innerHTML = text.replace(/\n/g, "<br>");
  };

  // ✅ UPDATED submit function with route and action
  const submit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate based on channel
    if (form.channel === "EMAIL" && !form.subject?.trim()) {
      alert("Subject is required for EMAIL templates");
      return;
    }

    if (
      (form.channel === "PUSH" || form.channel === "IN_APP") &&
      !form.title?.trim()
    ) {
      alert("Title is required for PUSH and IN_APP templates");
      return;
    }

    // ✅ NEW: Validate route for PUSH/IN_APP
    if (
      (form.channel === "PUSH" || form.channel === "IN_APP") &&
      !form.route?.trim()
    ) {
      alert("Route is required for PUSH and IN_APP templates");
      return;
    }

    // Auto-extract variables from body
    const extractedVars = extractVariablesFromBody(form.body);

    // Build payload based on channel
    const payload: any = {
      templateId: form.templateId.trim(),
      name: form.name.trim(),
      channel: form.channel,
      body: form.body,
      variables: extractedVars,
      isActive: true,
    };

    // Only add subject for EMAIL templates
    if (form.channel === "EMAIL") {
      payload.subject = form.subject?.trim() || "";
    }
    // Only add title, route, action for PUSH/IN_APP templates
    else if (form.channel === "PUSH" || form.channel === "IN_APP") {
      payload.title = form.title?.trim() || "";
      payload.route = form.route?.trim() || "";
      payload.action = form.action?.trim() || "";
    }

    try {
      if (editing) {
        await notificationApi.templates.update(editing.templateId, payload);
        alert("Template Updated Successfully");
      } else {
        await notificationApi.templates.create(payload);
        alert("Template Created Successfully");
      }
      setOpen(false);
      await remote.reload();
    } catch (error) {
      console.error(error);
      alert("Failed to save template: " + getError(error));
    }
  };

  // ✅ UPDATED: renderVariableGroups with "Show All" info
  const renderVariableGroups = () => {
    if (loadingVars)
      return <p className="nm-vars-empty">Loading variables...</p>;
    if (varError)
      return (
        <p className="nm-vars-empty" style={{ color: "#dc3545" }}>
          ❌ {varError}
        </p>
      );
    if (!variables)
      return (
        <p className="nm-vars-empty">
          Select a business event to see variables
        </p>
      );
    if (!variables.variables || variables.variables.length === 0) {
      return <p className="nm-vars-empty">No variables found for this event</p>;
    }

    const validVariables = variables.variables.filter(
      (v) =>
        v &&
        typeof v === "object" &&
        typeof v.name === "string" &&
        v.name.trim() !== "",
    );

    if (validVariables.length === 0) {
      return <p className="nm-vars-empty">No valid variables found</p>;
    }

    const grouped = variables.grouped || {};
    const groupKeys = Object.keys(grouped);

    // ✅ Show note about filtered vs all
    const showFilterNote =
      variables.originalCount && variables.originalCount > variables.total;

    return (
      <div>
        {showFilterNote && (
          <div className="nm-vars-filter-note">
            <small>
              Showing {variables.total} of {variables.originalCount} variables
              {!showAllVariables && (
                <span>
                  {" "}
                  — <strong>Toggle "Show all" below to see everything</strong>
                </span>
              )}
            </small>
          </div>
        )}

        {groupKeys.length === 0 ? (
          <div className="nm-vars-list">
            {validVariables.map((v) => (
              <VariableChip
                key={v.name}
                variable={v}
                onClick={insertVariable}
              />
            ))}
          </div>
        ) : (
          groupKeys
            .map((key) => {
              const group = grouped[key];
              if (!group || !group.variables) return null;

              const validGroupVars = group.variables.filter(
                (v) =>
                  v &&
                  typeof v === "object" &&
                  typeof v.name === "string" &&
                  v.name.trim() !== "",
              );

              if (validGroupVars.length === 0) return null;

              return (
                <div className="nm-var-group" key={key}>
                  <div className="nm-var-group-header">
                    <span>{group.label || key}</span>
                    <span className="nm-var-count">
                      {validGroupVars.length}
                    </span>
                  </div>
                  <div className="nm-vars-list">
                    {validGroupVars.map((v) => (
                      <VariableChip
                        key={v.name}
                        variable={v}
                        onClick={insertVariable}
                      />
                    ))}
                  </div>
                </div>
              );
            })
            .filter(Boolean)
        )}
      </div>
    );
  };

  return (
    <>
      <Toolbar
        title="Templates"
        search={search}
        setSearch={setSearch}
        reload={remote.reload}
        action={
          <button
            onClick={() => {
              setEditing(null);
              setForm(blank);
              setVariables(null);
              setShowAllVariables(false); // ✅ Reset toggle
              setOpen(true);
              setTimeout(updatePreview, 100);
            }}
          >
            + Template
          </button>
        }
      />
      <PageState
        loading={remote.loading}
        error={remote.error}
        empty={!rows.length}
      >
        <div className="nm-grid">
          {rows.map((x) => (
            <article className="nm-panel" key={x.templateId}>
              <div>
                <h3>{x.name}</h3>
                <p>
                  <code>{x.templateId}</code> · <Status value={x.channel} />
                </p>
                <p>{x.subject || x.title || "No subject/title"}</p>
                <small>Variables: {x.variables?.join(", ") || "None"}</small>
              </div>
              <div className="nm-actions">
                <button
                  onClick={() => {
                    setEditing(x);
                    const extractedVars = extractVariablesFromBody(
                      x.body || "",
                    );
                    setForm({
                      ...x,
                      subject: x.subject || "",
                      title: x.title || "",
                      variables: extractedVars,
                      route: x.route || "",
                      action: x.action || "",
                    });
                    setShowAllVariables(false); // ✅ Reset toggle
                    setOpen(true);
                    setTimeout(updatePreview, 100);
                  }}
                >
                  Edit
                </button>
                <button
                  className="nm-danger"
                  onClick={async () => {
                    if (window.confirm("Delete this template?")) {
                      await notificationApi.templates.remove(x.templateId);
                      remote.reload();
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      </PageState>
      {open && (
        <Modal
          title={editing ? "Edit Template" : "New Template"}
          close={() => setOpen(false)}
        >
          <form className="nm-form" onSubmit={submit}>
            <label>
              <span>Template ID *</span>
              <input
                required
                disabled={!!editing}
                value={form.templateId}
                onChange={(e) =>
                  setForm({ ...form, templateId: e.target.value })
                }
                placeholder="e.g., WELCOME_EMAIL"
              />
            </label>
            <label>
              <span>Name *</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Welcome Email"
              />
            </label>
            <label>
              <span>Channel *</span>
              <select
                value={form.channel}
                onChange={(e) =>
                  setForm({
                    ...form,
                    channel: e.target.value as Channel,
                  })
                }
              >
                {["EMAIL", "SMS", "PUSH", "IN_APP"].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>

            {form.channel === "EMAIL" && (
              <label className="nm-full">
                <span>Subject * (required for EMAIL)</span>
                <input
                  required
                  value={form.subject || ""}
                  onChange={(e) =>
                    setForm({ ...form, subject: e.target.value })
                  }
                  placeholder="e.g., Welcome to Qavach!"
                />
              </label>
            )}

            {(form.channel === "PUSH" || form.channel === "IN_APP") && (
              <>
                <label className="nm-full">
                  <span>Title * (required for PUSH/IN_APP)</span>
                  <input
                    required
                    value={form.title || ""}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                    placeholder="e.g., Welcome to Qavach!"
                  />
                </label>

                {/* ✅ ROUTE DROPDOWN */}
                <label className="nm-full">
                  <span>Navigation Route * (required for PUSH/IN_APP)</span>
                  <select
                    required
                    value={form.route || ""}
                    onChange={(e) =>
                      setForm({ ...form, route: e.target.value })
                    }
                  >
                    <option value="">Select a navigation route...</option>
                    {ROUTE_OPTIONS.map((route) => (
                      <option key={route.value} value={route.value}>
                        {route.label}
                      </option>
                    ))}
                  </select>
                  <small>
                    Where the user should be directed when tapping the
                    notification
                  </small>
                </label>

                {/* ✅ ACTION INPUT - FIXED placeholder */}
                <label className="nm-full">
                  <span>Action (Parameter Mapping)</span>
                  <input
                    value={form.action || ""}
                    onChange={(e) =>
                      setForm({ ...form, action: e.target.value })
                    }
                    placeholder={
                      "e.g., appointmentId={{response.data._id}},rescheduleConfigId={{response.data._id}}"
                    }
                  />
                  <small>
                    Parameters to pass to the route. Format: paramName=
                    {"{{path.to.value}}"}
                  </small>
                </label>
              </>
            )}

            {/* ✅ SMS doesn't need route/action */}
            {form.channel === "SMS" && (
              <div className="nm-hint">
                <small>
                  SMS notifications don't support navigation routes.
                </small>
              </div>
            )}

            <label className="nm-full">
              <span>Body *</span>
              <textarea
                id="templateBody"
                required
                rows={8}
                value={form.body}
                onChange={(e) => {
                  setForm({ ...form, body: e.target.value });
                  setTimeout(updatePreview, 50);
                }}
                placeholder="Enter your template content here..."
              />
            </label>

            {/* ✅ UPDATED: Available Variables section with "Show All" toggle */}
            <div className="nm-full nm-vars-section">
              <div className="nm-vars-header">
                <h4>Available Variables</h4>
                <div className="nm-vars-controls">
                  <select
                    onChange={(e) => {
                      setShowAllVariables(false);
                      loadVariables(e.target.value, false);
                    }}
                    defaultValue=""
                  >
                    <option value="">Select a business event...</option>
                    {businessEvents.map((e) => (
                      <option key={e._id} value={e.code}>
                        {e.code} - {e.name}
                      </option>
                    ))}
                  </select>
                  {variables &&
                    variables.variables &&
                    variables.variables.length > 0 && (
                      <label className="nm-show-all-toggle">
                        <input
                          type="checkbox"
                          checked={showAllVariables}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setShowAllVariables(checked);
                            // Reload with the new showAll value
                            const select = document.querySelector(
                              ".nm-vars-controls select",
                            ) as HTMLSelectElement;
                            if (select && select.value) {
                              loadVariables(select.value, checked);
                            }
                          }}
                        />
                        <span>
                          Show all {variables.originalCount || variables.total}{" "}
                          variables
                        </span>
                      </label>
                    )}
                </div>
              </div>
              <div className="nm-vars-container">{renderVariableGroups()}</div>
            </div>

            <div className="nm-full nm-preview-section">
              <h4>Live Preview</h4>
              <div
                id="templatePreview"
                className="nm-preview"
                style={{
                  minHeight: "150px",
                  background: "#f8f9fa",
                  border: "1px dashed #bbb",
                  borderRadius: "6px",
                  padding: "16px",
                  whiteSpace: "pre-wrap",
                  lineHeight: "1.7",
                }}
              >
                Preview appears here...
              </div>
            </div>

            <button type="submit" className="nm-full">
              Save Template
            </button>
          </form>
        </Modal>
      )}
    </>
  );
} // <-- THIS CLOSING BRACE IS CRITICAL

// ============================================================
// VARIABLE CHIP COMPONENT
// ============================================================

// const VariableChip = ({
//   variable,
//   onClick,
// }: {
//   variable: VariableDefinition;
//   onClick: (name: string) => void;
// }) => {
//   // ✅ Ensure variable.name is a string
//   const name =
//     typeof variable.name === "string"
//       ? variable.name
//       : String(variable.name || "");

//   if (!name) return null;

//   return (
//     <span
//       className="nm-var-chip"
//       onClick={() => onClick(name)}
//       title={
//         variable.example !== undefined && variable.example !== null
//           ? `Example: ${variable.example}`
//           : ""
//       }
//     >
//       {`{{${name}}}`}
//       <span className="nm-var-chip-icon">📋</span>
//     </span>
//   );
// };

const VariableChip = ({
  variable,
  onClick,
}: {
  variable: VariableDefinition;
  onClick: (name: string) => void;
}) => {
  const name =
    typeof variable.name === "string"
      ? variable.name
      : String(variable.name || "");

  if (!name) return null;

  // // Match the same rule as insertVariable
  // const display =
  //   name.startsWith("audit.") || name.startsWith("jwt.")
  //     ? `{{[${name}]}}`
  //     : `{{${name}}}`;

  const display = wrapVariable(name);

  return (
    <span
      className="nm-var-chip"
      onClick={() => onClick(name)}
      title={
        variable.example !== undefined && variable.example !== null
          ? `Example: ${variable.example}`
          : ""
      }
    >
      {display}
      <span className="nm-var-chip-icon">📋</span>
    </span>
  );
};

// ============================================================
// RULES PAGE
// ============================================================

function RulesPage() {
  const remote = useRemote(notificationApi.rules.list, []);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<NotificationRule | null>(null);
  const [businessEvents, setBusinessEvents] = useState<BusinessEvent[]>([]);
  const [allTemplates, setAllTemplates] = useState<NotificationTemplate[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  // ✅ State for dropdown options
  const [services, setServices] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // ✅ Load services when modal opens
  useEffect(() => {
    if (open) {
      loadDropdownOptions();
    }
  }, [open]);

  const loadDropdownOptions = async () => {
    setLoadingDropdowns(true);
    try {
      const servicesRes = await notificationApi.discovery.services();
      setServices(servicesRes || []);
    } catch (error) {
      console.error("Failed to load dropdown options:", error);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // ✅ Load actions when service changes
  const handleServiceChange = async (service: string) => {
    setForm({
      ...form,
      stopAuditMatch: {
        ...form.stopAuditMatch,
        service: service,
        action: "", // Reset action when service changes
      },
    });
    if (service) {
      try {
        const actionsRes = await notificationApi.discovery.actions(service);
        setActions(actionsRes || []);
      } catch (error) {
        console.error("Failed to load actions:", error);
        setActions([]);
      }
    } else {
      setActions([]);
    }
  };

  // ✅ Just suggestions - NOT hardcoded requirements!
  const getSuggestedAuditMatch = (
    field: string,
  ): Partial<StopAuditMatch> | null => {
    const suggestions: Record<string, Partial<StopAuditMatch>> = {
      hasChild: {
        service: "parent-child",
        action: "POST /children",
        status: "SUCCESS",
      },
      hasAppointment: {
        service: "appointment",
        action: "POST /api/appointments",
        status: "SUCCESS",
      },
      onboardingComplete: {
        service: "user-management",
        action: "ONBOARDING_COMPLETED",
        status: "SUCCESS",
      },
      screeningComplete: {
        service: "appointment",
        action: "PATCH /api/appointments/:id/screened",
        status: "SUCCESS",
      },
      reportPublished: {
        service: "report",
        action: "POST /api/reports",
        status: "SUCCESS",
      },
      paymentSuccess: {
        service: "payment",
        action: "POST /verify",
        status: "SUCCESS",
      },
      inactive: {
        service: "user",
        action: "INACTIVITY_CHECK",
        status: "SUCCESS",
      },
    };
    return suggestions[field] || null;
  };

  const getAuditMatchDescription = (field: string): string => {
    const descriptions: Record<string, string> = {
      hasChild: "User has successfully added a child",
      hasAppointment: "User has successfully booked an appointment",
      onboardingComplete: "User has completed onboarding",
      screeningComplete: "User has completed screening",
      reportPublished: "Report has been published",
      paymentSuccess: "Payment was successful",
      inactive: "User has been inactive for configured days",
      custom: "Custom audit log matching",
    };
    return descriptions[field] || field;
  };

  // ✅ Type definitions
  type AuditMatchOperator =
    | "EXISTS"
    | "NOT_EXISTS"
    | "EQUALS"
    | "NOT_EQUALS"
    | "GREATER_THAN"
    | "LESS_THAN"
    | "CONTAINS";

  type StopAuditMatch = {
    service: string;
    action: string;
    module: string;
    status: "SUCCESS" | "FAILURE";
    fieldPath: string;
    expectedValue: any;
    operator: AuditMatchOperator;
  };

  const [form, setForm] = useState({
    name: "",
    businessEventId: "",
    recipientType: "ACTOR" as RecipientType,
    priority: "MEDIUM" as Priority,
    scheduleType: "INSTANT" as ScheduleType,
    delay: "",
    cronExpression: "",
    campaignEnabled: true,
    stopField: "hasChild",
    stopOperator: "EQUALS" as
      | "EXISTS"
      | "NOT_EXISTS"
      | "EQUALS"
      | "GREATER_THAN"
      | "LESS_THAN",
    stopValue: "",
    stopAuditMatch: {
      service: "",
      action: "",
      module: "",
      status: "SUCCESS" as "SUCCESS" | "FAILURE",
      fieldPath: "",
      expectedValue: "",
      operator: "EQUALS" as AuditMatchOperator,
    } as StopAuditMatch,
    maxDays: 5,
    interval: "1d",
    fallbackTemplate: "",
    templateGroups: [] as TemplateGroup[],
    channelTemplates: {
      EMAIL: { enabled: true, templateId: "" },
      SMS: { enabled: false, templateId: "" },
      PUSH: { enabled: true, templateId: "" },
      IN_APP: { enabled: true, templateId: "" },
    },
  });

  const rows = (remote.data || []).filter((x) =>
    JSON.stringify(x).toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    if (open) {
      const loadData = async () => {
        setLoadingData(true);
        try {
          const [events, templates] = await Promise.all([
            notificationApi.events.list(),
            notificationApi.templates.list(),
          ]);
          setBusinessEvents(events);
          setAllTemplates(templates);
        } catch (e) {
          console.error("Failed to load rule data:", e);
        } finally {
          setLoadingData(false);
        }
      };
      loadData();
    }
  }, [open]);

  const initializeTemplateGroups = () => {
    const defaults = [
      { name: "Day 1 - Welcome", from: 1, to: 1 },
      { name: "Day 2 - Why Track Health", from: 2, to: 2 },
      { name: "Day 3 - Growth Monitoring", from: 3, to: 3 },
      { name: "Day 4 - Nutrition Tips", from: 4, to: 4 },
      { name: "Day 5 - Vaccination Reminders", from: 5, to: 5 },
    ];
    return defaults.map((g) => ({
      name: g.name,
      days: { from: g.from, to: g.to },
      templateId: "",
      channelOverrides: [] as Array<{ channel: Channel; templateId: string }>,
    }));
  };

  const [templateGroups, setTemplateGroups] = useState<TemplateGroup[]>(() =>
    initializeTemplateGroups(),
  );

  const addTemplateGroup = () => {
    const newGroup: TemplateGroup = {
      name: `Day ${templateGroups.length + 1}`,
      days: { from: templateGroups.length + 1, to: templateGroups.length + 1 },
      templateId: "",
      channelOverrides: [],
    };
    setTemplateGroups([...templateGroups, newGroup]);
  };

  const removeTemplateGroup = (index: number) => {
    if (templateGroups.length <= 1) {
      alert("You need at least one template group");
      return;
    }
    setTemplateGroups(templateGroups.filter((_, i) => i !== index));
  };

  const updateTemplateGroup = (
    index: number,
    field: string,
    value: unknown,
  ) => {
    const updated = [...templateGroups];
    updated[index] = { ...updated[index], [field]: value };
    setTemplateGroups(updated);
  };

  const updateGroupTemplate = (
    index: number,
    channel: Channel,
    templateId: string,
  ) => {
    const updated = [...templateGroups];
    if (channel === "EMAIL") {
      updated[index].templateId = templateId;
    } else {
      const overrides = updated[index].channelOverrides || [];
      const existing = overrides.findIndex((o) => o.channel === channel);
      if (existing >= 0) {
        if (templateId) {
          overrides[existing].templateId = templateId;
        } else {
          overrides.splice(existing, 1);
        }
      } else if (templateId) {
        overrides.push({ channel, templateId });
      }
      updated[index].channelOverrides = overrides;
    }
    setTemplateGroups(updated);
  };

  const getGroupTemplate = (group: TemplateGroup, channel: Channel): string => {
    if (channel === "EMAIL") {
      return group.templateId;
    }
    const override = group.channelOverrides?.find((o) => o.channel === channel);
    return override?.templateId || "";
  };

  // const edit = async (x: NotificationRule) => {
  //   setEditing(x);
  //   const businessEventId =
  //     typeof x.businessEventDefinitionId === "object"
  //       ? x.businessEventDefinitionId?._id || ""
  //       : x.businessEventDefinitionId || "";

  //   const recipientType =
  //     (x.recipientGroupId as any)?.recipients?.[0]?.type || "ACTOR";

  //   // ✅ Load existing auditMatch from the rule (if it exists)
  //   const existingAuditMatch = (x.schedule?.campaign?.stopCondition as any)
  //     ?.auditMatch;

  //   // ✅ If there's an existing auditMatch, use it. Otherwise, use empty values.
  //   const stopAuditMatch: StopAuditMatch = existingAuditMatch
  //     ? {
  //         service: existingAuditMatch.service || "",
  //         action: existingAuditMatch.action || "",
  //         module: existingAuditMatch.module || "",
  //         status: (existingAuditMatch.status || "SUCCESS") as
  //           | "SUCCESS"
  //           | "FAILURE",
  //         fieldPath: existingAuditMatch.fieldPath || "",
  //         expectedValue: existingAuditMatch.expectedValue ?? "",
  //         operator: (existingAuditMatch.operator ||
  //           "EQUALS") as AuditMatchOperator,
  //       }
  //     : {
  //         service: "",
  //         action: "",
  //         module: "",
  //         status: "SUCCESS" as "SUCCESS" | "FAILURE",
  //         fieldPath: "",
  //         expectedValue: "",
  //         operator: "EQUALS" as AuditMatchOperator,
  //       };

  const edit = async (x: NotificationRule) => {
    setEditing(x);

    const businessEventId =
      typeof x.businessEventDefinitionId === "object"
        ? x.businessEventDefinitionId?._id || ""
        : x.businessEventDefinitionId || "";

    const recipientType =
      (x.recipientGroupId as any)?.recipients?.[0]?.type || "ACTOR";

    const stopFieldFromRule =
      x.schedule?.campaign?.stopCondition?.field || "hasChild";

    let existingAuditMatch = (x.schedule?.campaign?.stopCondition as any)
      ?.auditMatch;

    // ✅ Backward-compat: if the rule predates dynamic auditMatches,
    //    hydrate from the suggestion table so the admin just clicks Save.
    if (!existingAuditMatch && stopFieldFromRule !== "custom") {
      existingAuditMatch =
        getSuggestedAuditMatch(stopFieldFromRule) || undefined;
    }

    const stopAuditMatch: StopAuditMatch = existingAuditMatch
      ? {
          service: existingAuditMatch.service || "",
          action: existingAuditMatch.action || "",
          module: existingAuditMatch.module || "",
          status: (existingAuditMatch.status || "SUCCESS") as
            | "SUCCESS"
            | "FAILURE",
          fieldPath: existingAuditMatch.fieldPath || "",
          expectedValue: existingAuditMatch.expectedValue ?? "",
          operator: (existingAuditMatch.operator ||
            "EQUALS") as AuditMatchOperator,
        }
      : {
          service: "",
          action: "",
          module: "",
          status: "SUCCESS",
          fieldPath: "",
          expectedValue: "",
          operator: "EQUALS",
        };

    setForm({
      name: x.name,
      businessEventId: businessEventId,
      recipientType: recipientType,
      priority: (x.priority as Priority) || "MEDIUM",
      scheduleType: (x.schedule?.type as ScheduleType) || "INSTANT",
      delay: x.schedule?.delay || "",
      cronExpression: x.schedule?.cronExpression || "",
      campaignEnabled: x.schedule?.campaign?.enabled !== false,
      stopField: x.schedule?.campaign?.stopCondition?.field || "hasChild",
      stopOperator:
        (x.schedule?.campaign?.stopCondition?.operator as
          | "EXISTS"
          | "NOT_EXISTS"
          | "EQUALS"
          | "GREATER_THAN"
          | "LESS_THAN") || "EQUALS",
      stopValue: x.schedule?.campaign?.stopCondition?.value || "",
      stopAuditMatch: stopAuditMatch,
      maxDays: x.schedule?.campaign?.maxIterations || 5,
      interval: x.schedule?.campaign?.interval || "1d",
      fallbackTemplate: x.schedule?.campaign?.fallbackTemplateId || "",
      templateGroups:
        x.schedule?.campaign?.templateGroups || initializeTemplateGroups(),
      channelTemplates: {
        EMAIL: { enabled: true, templateId: "" },
        SMS: { enabled: false, templateId: "" },
        PUSH: { enabled: true, templateId: "" },
        IN_APP: { enabled: true, templateId: "" },
      },
    });

    // Load actions for the service if it exists
    if (stopAuditMatch.service) {
      try {
        const actionsRes = await notificationApi.discovery.actions(
          stopAuditMatch.service,
        );
        setActions(actionsRes || []);
      } catch (error) {
        setActions([]);
      }
    }

    const templateGroup = x.templateGroupId as any;
    if (templateGroup?.templates) {
      const newChannelTemplates = { ...form.channelTemplates };
      templateGroup.templates.forEach((t: any) => {
        const channel = t.channel as Channel;
        const templateId =
          typeof t.templateId === "object"
            ? t.templateId?._id || t.templateId?.templateId || ""
            : t.templateId || "";
        if (newChannelTemplates[channel]) {
          newChannelTemplates[channel] = { enabled: true, templateId };
        }
      });
      setForm((prev) => ({ ...prev, channelTemplates: newChannelTemplates }));
    }

    if (x.schedule?.campaign?.templateGroups) {
      setTemplateGroups(x.schedule.campaign.templateGroups);
    }

    setOpen(true);
  };

  const isCampaign = form.scheduleType === "RECURRING";
  const isCustomStop = form.stopField === "custom";

  const submit = async (e: FormEvent) => {
    e.preventDefault();

    if (!form.name) {
      alert("Rule Name is required");
      return;
    }
    if (!form.businessEventId) {
      alert("Please select a Business Event");
      return;
    }

    const channelMapping: Array<{
      recipientType: RecipientType;
      channel: Channel;
      templateId: string;
    }> = [];
    const channels: Channel[] = ["EMAIL", "SMS", "PUSH", "IN_APP"];
    for (const ch of channels) {
      const config = form.channelTemplates[ch];
      if (config?.enabled && config.templateId) {
        channelMapping.push({
          recipientType: form.recipientType,
          channel: ch,
          templateId: config.templateId,
        });
      }
    }

    if (!isCampaign && channelMapping.length === 0) {
      alert("Please select at least one channel with a template");
      return;
    }

    const schedule: any = { type: form.scheduleType };

    if (form.scheduleType === "DELAYED") {
      schedule.delay = form.delay || "1h";
      // } else if (form.scheduleType === "RECURRING") {
      //   const campaignConfig: CampaignConfig = {
      //     enabled: true,
      //     stopCondition: {
      //       field: form.stopField,
      //       operator: form.stopOperator,
      //     },
      //     maxIterations: form.maxDays,
      //     interval: form.interval,
      //     templateGroups: templateGroups,
      //   };

      //   if (form.stopOperator === "EQUALS" && form.stopValue) {
      //     campaignConfig.stopCondition.value = form.stopValue;
      //   }

      //   // ✅ ONLY save auditMatch if Custom condition is selected AND has values
      //   if (isCustomStop) {
      //     const hasAuditMatchValues = Object.values(form.stopAuditMatch).some(
      //       (val) => val !== "" && val !== null && val !== undefined,
      //     );
      //     if (hasAuditMatchValues) {
      //       campaignConfig.stopCondition.auditMatch = form.stopAuditMatch;
      //     }
      //   }

      //   if (form.fallbackTemplate) {
      //     campaignConfig.fallbackTemplateId = form.fallbackTemplate;
      //   }

      //   schedule.campaign = campaignConfig;
      // }
    } else if (form.scheduleType === "RECURRING") {
      // ────────────────────────────────────────────────────────────────
      // BUILD THE AUDIT MATCH
      //
      // The form already holds a fully-populated `stopAuditMatch` for both
      // predefined conditions (auto-filled via getSuggestedAuditMatch) and
      // custom conditions (manually filled). We now ALWAYS include it in
      // the payload. The backend requires it.
      // ────────────────────────────────────────────────────────────────
      const am = form.stopAuditMatch || ({} as StopAuditMatch);

      if (!am.service || !am.action) {
        alert(
          "Stop condition is incomplete.\n\n" +
            'Please select a predefined stop condition, or use "Custom condition" ' +
            "and pick a Service + Action from the dropdowns.",
        );
        return;
      }

      // Normalize the operator based on whether a field check is present.
      // (Backend enforces this too, but doing it here gives better UX.)
      const normalizedOperator =
        am.fieldPath && am.fieldPath.trim()
          ? am.operator || "EQUALS"
          : am.operator === "NOT_EXISTS"
            ? "NOT_EXISTS"
            : "EXISTS";

      const cleanedAuditMatch = {
        service: am.service.trim(),
        action: am.action.trim(),
        ...(am.module?.trim() ? { module: am.module.trim() } : {}),
        status: am.status || "SUCCESS",
        ...(am.fieldPath?.trim() ? { fieldPath: am.fieldPath.trim() } : {}),
        ...(am.fieldPath?.trim() &&
        am.expectedValue !== "" &&
        am.expectedValue !== undefined
          ? { expectedValue: am.expectedValue }
          : {}),
        operator: normalizedOperator,
      };

      const campaignConfig: CampaignConfig = {
        enabled: true,
        stopCondition: {
          field: form.stopField,
          operator: form.stopOperator,
          // ✅ ALWAYS include the audit match — backend now requires it
          auditMatch: cleanedAuditMatch,
        },
        maxIterations: form.maxDays,
        interval: form.interval,
        templateGroups: templateGroups,
      };

      // Optional legacy value (kept for display only)
      if (form.stopOperator === "EQUALS" && form.stopValue) {
        campaignConfig.stopCondition.value = form.stopValue;
      }

      if (form.fallbackTemplate) {
        campaignConfig.fallbackTemplateId = form.fallbackTemplate;
      }

      schedule.campaign = campaignConfig;
    }

    try {
      const groups = await notificationApi.recipientGroups.list();
      let recipientGroupId = groups.find(
        (g) => g.businessEventDefinitionId === form.businessEventId,
      )?._id;

      if (!recipientGroupId) {
        const newGroup = await notificationApi.recipientGroups.create({
          businessEventDefinitionId: form.businessEventId,
          name: `Recipients for ${form.name}`,
          groupType: "DEFAULT",
          priority: 1,
          enabled: true,
          recipients: [
            {
              type: form.recipientType,
              order: 1,
              sourcePath:
                form.recipientType === "AFFECTED_USER" ? "metadata" : "actor",
              fields:
                form.recipientType === "AFFECTED_USER"
                  ? { userId: "affectedUserIds" }
                  : { userId: "userId", email: "email", name: "name" },
            },
          ],
        });
        recipientGroupId = (newGroup as any)._id;
      }

      const templateGroupsList = await notificationApi.templateGroups.list();
      let templateGroupId = templateGroupsList.find(
        (g) => g.businessEventDefinitionId === form.businessEventId,
      )?._id;

      if (templateGroupId) {
        if (!isCampaign) {
          await notificationApi.templateGroups.update(templateGroupId, {
            templates: channelMapping,
          });
        }
      } else {
        const templatesForGroup = isCampaign ? [] : channelMapping;
        const newGroup = await notificationApi.templateGroups.create({
          businessEventDefinitionId: form.businessEventId,
          name: `Templates for ${form.name}`,
          groupType: "DEFAULT",
          enabled: true,
          templates: templatesForGroup,
        });
        templateGroupId = (newGroup as any)._id;
      }

      const rulePayload = {
        businessEventDefinitionId: form.businessEventId,
        recipientGroupId: recipientGroupId,
        templateGroupId: templateGroupId,
        name: form.name,
        priority: form.priority,
        enabled: true,
        conditions: [],
        stopConditions: [],
        schedule: schedule,
      };

      if (editing) {
        await notificationApi.rules.update(editing._id, rulePayload as any);
      } else {
        await notificationApi.rules.create(rulePayload as any);
      }

      setOpen(false);
      remote.reload();
    } catch (err) {
      alert("Failed to save rule: " + getError(err));
    }
  };

  const renderCampaignGroups = () => {
    return templateGroups.map((group, index) => (
      <div className="nm-campaign-group" key={index}>
        <div className="nm-campaign-group-header">
          <div className="nm-campaign-group-fields">
            <label>
              <span>Group Name</span>
              <input
                value={group.name}
                onChange={(e) =>
                  updateTemplateGroup(index, "name", e.target.value)
                }
              />
            </label>
            <label>
              <span>Days</span>
              <div className="nm-day-range">
                <input
                  type="number"
                  min={1}
                  value={group.days.from}
                  onChange={(e) =>
                    updateTemplateGroup(index, "days", {
                      ...group.days,
                      from: parseInt(e.target.value) || 1,
                    })
                  }
                />
                <span>to</span>
                <input
                  type="number"
                  min={1}
                  value={group.days.to}
                  onChange={(e) =>
                    updateTemplateGroup(index, "days", {
                      ...group.days,
                      to: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
            </label>
          </div>
          <button
            type="button"
            className="nm-danger"
            onClick={() => removeTemplateGroup(index)}
          >
            ✕ Remove
          </button>
        </div>

        <div className="nm-campaign-group-channels">
          {(["EMAIL", "PUSH", "IN_APP"] as Channel[]).map((ch) => (
            <label key={ch}>
              <span>{ch}</span>
              <select
                value={getGroupTemplate(group, ch)}
                onChange={(e) => updateGroupTemplate(index, ch, e.target.value)}
              >
                <option value="">Select {ch} template...</option>
                {allTemplates
                  .filter((t) => t.channel === ch && t.isActive !== false)
                  .map((t) => (
                    <option key={t._id || t.templateId} value={t.templateId}>
                      {t.templateId || t.name} (v{t.version || 1})
                    </option>
                  ))}
              </select>
            </label>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <>
      <Toolbar
        title="Notification Rules"
        search={search}
        setSearch={setSearch}
        reload={remote.reload}
        action={
          <button
            onClick={() => {
              setEditing(null);
              setForm({
                name: "",
                businessEventId: "",
                recipientType: "ACTOR",
                priority: "MEDIUM",
                scheduleType: "INSTANT",
                delay: "",
                cronExpression: "",
                campaignEnabled: true,
                stopField: "hasChild",
                stopOperator: "EQUALS",
                stopValue: "",
                stopAuditMatch: {
                  service: "",
                  action: "",
                  module: "",
                  status: "SUCCESS",
                  fieldPath: "",
                  expectedValue: "",
                  operator: "EQUALS",
                },
                maxDays: 5,
                interval: "1d",
                fallbackTemplate: "",
                templateGroups: [],
                channelTemplates: {
                  EMAIL: { enabled: true, templateId: "" },
                  SMS: { enabled: false, templateId: "" },
                  PUSH: { enabled: true, templateId: "" },
                  IN_APP: { enabled: true, templateId: "" },
                },
              });
              setTemplateGroups(initializeTemplateGroups());
              setActions([]);
              setOpen(true);
            }}
          >
            + Rule
          </button>
        }
      />
      <PageState
        loading={remote.loading}
        error={remote.error}
        empty={!rows.length}
      >
        <div className="nm-grid">
          {rows.map((x) => (
            <article className="nm-panel" key={x._id}>
              <div>
                <h3>{x.name}</h3>
                <p>
                  {(() => {
                    const def = x.businessEventDefinitionId;
                    if (typeof def === "object" && def !== null) {
                      return `${def.code} - ${def.name}`;
                    }
                    return x.businessEventCode || x.eventCode || "-";
                  })()}{" "}
                  · priority {x.priority ?? "-"}
                </p>
                <p>
                  {x.schedule?.type || "INSTANT"} · {x.recipientType || "-"}
                </p>
                {x.schedule?.type === "RECURRING" && x.schedule?.campaign && (
                  <p className="nm-campaign-badge">
                    📋 Campaign:{" "}
                    {x.schedule.campaign.templateGroups?.length || 0} day groups
                  </p>
                )}
                <Status value={x.enabled ? "Enabled" : "Disabled"} />
              </div>
              <div className="nm-actions">
                <button onClick={() => edit(x)}>Edit</button>
                <button
                  className="nm-secondary"
                  onClick={async () => {
                    await notificationApi.rules.toggle(x._id);
                    remote.reload();
                  }}
                >
                  {x.enabled ? "Disable" : "Enable"}
                </button>
                <button
                  className="nm-danger"
                  onClick={async () => {
                    if (window.confirm("Delete this rule?")) {
                      await notificationApi.rules.remove(x._id);
                      remote.reload();
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      </PageState>
      {open && (
        <Modal
          title={editing ? "Edit Rule" : "New Rule"}
          close={() => setOpen(false)}
        >
          {loadingData ? (
            <div className="nm-state">Loading data...</div>
          ) : (
            <form className="nm-form" onSubmit={submit}>
              <label className="nm-full">
                <span>Rule Name *</span>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label className="nm-full">
                <span>Business Event *</span>
                <select
                  required
                  value={form.businessEventId}
                  onChange={(e) =>
                    setForm({ ...form, businessEventId: e.target.value })
                  }
                >
                  <option value="">Select a Business Event</option>
                  {businessEvents.map((e) => (
                    <option key={e._id} value={e._id}>
                      {e.code} - {e.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Recipient Type</span>
                <select
                  value={form.recipientType}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      recipientType: e.target.value as RecipientType,
                    })
                  }
                >
                  <option value="ACTOR">
                    ACTOR - Person who performed action
                  </option>
                  <option value="AFFECTED_USER">
                    AFFECTED_USER - User affected by the event
                  </option>
                  <option value="PARENT">PARENT - Parent of child</option>
                  <option value="DOCTOR">DOCTOR - Assigned doctor</option>
                  <option value="ASSIGNED_USER">
                    ASSIGNED_USER - Assigned staff
                  </option>
                  <option value="ROLE">ROLE - Users with specific role</option>
                </select>
              </label>

              <label>
                <span>Priority</span>
                <select
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: e.target.value as Priority })
                  }
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
              </label>

              <label>
                <span>Schedule Type</span>
                <select
                  value={form.scheduleType}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      scheduleType: e.target.value as ScheduleType,
                    })
                  }
                >
                  <option value="INSTANT">Instant</option>
                  <option value="DELAYED">Delayed</option>
                  <option value="RECURRING">Recurring (Campaign)</option>
                </select>
              </label>

              {form.scheduleType === "DELAYED" && (
                <label className="nm-full">
                  <span>Delay *</span>
                  <input
                    required
                    value={form.delay}
                    onChange={(e) =>
                      setForm({ ...form, delay: e.target.value })
                    }
                    placeholder="e.g., 1h, 30m, 1d"
                  />
                  <small>Format: 30m, 2h, 1d</small>
                </label>
              )}

              {isCampaign && (
                <div className="nm-full nm-campaign-config">
                  <h4>📋 Campaign Configuration</h4>

                  <div className="nm-campaign-row">
                    <label>
                      <span>Stop Condition Field</span>
                      <select
                        value={form.stopField}
                        onChange={(e) => {
                          const field = e.target.value;
                          setForm({ ...form, stopField: field });
                          // ✅ Auto-populate SUGGESTIONS for predefined fields
                          if (field !== "custom") {
                            const suggestion = getSuggestedAuditMatch(field);
                            if (suggestion) {
                              setForm((prev) => ({
                                ...prev,
                                stopAuditMatch: {
                                  ...prev.stopAuditMatch,
                                  ...suggestion,
                                },
                              }));
                              // Load actions for the suggested service
                              if (suggestion.service) {
                                handleServiceChange(suggestion.service);
                              }
                            }
                          } else {
                            // Reset audit match for custom
                            setForm((prev) => ({
                              ...prev,
                              stopAuditMatch: {
                                service: "",
                                action: "",
                                module: "",
                                status: "SUCCESS",
                                fieldPath: "",
                                expectedValue: "",
                                operator: "EQUALS",
                              },
                            }));
                            setActions([]);
                          }
                        }}
                      >
                        <option value="hasChild">User has child</option>
                        <option value="hasAppointment">
                          User has appointment
                        </option>
                        <option value="onboardingComplete">
                          Onboarding complete
                        </option>
                        <option value="screeningComplete">
                          Screening complete
                        </option>
                        <option value="reportPublished">
                          Report published
                        </option>
                        <option value="paymentSuccess">
                          Payment successful
                        </option>
                        <option value="inactive">User inactive</option>
                        <option value="custom">Custom condition</option>
                      </select>
                    </label>
                    <label>
                      <span>Operator</span>
                      <select
                        value={form.stopOperator}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            stopOperator: e.target.value as
                              | "EXISTS"
                              | "NOT_EXISTS"
                              | "EQUALS"
                              | "GREATER_THAN"
                              | "LESS_THAN",
                          })
                        }
                      >
                        <option value="EXISTS">Exists</option>
                        <option value="NOT_EXISTS">Does not exist</option>
                        <option value="EQUALS">Equals</option>
                        <option value="GREATER_THAN">Greater than</option>
                        <option value="LESS_THAN">Less than</option>
                      </select>
                    </label>
                    {form.stopOperator === "EQUALS" && (
                      <label>
                        <span>Value</span>
                        <input
                          value={form.stopValue}
                          onChange={(e) =>
                            setForm({ ...form, stopValue: e.target.value })
                          }
                          placeholder="Value"
                        />
                      </label>
                    )}
                  </div>

                  {/* ✅ ONLY SHOW for Custom condition */}
                  {isCustomStop && (
                    <div className="nm-custom-audit-match">
                      <h5>🔍 Custom Audit Log Matching</h5>
                      <div className="nm-audit-match-hint">
                        <small>
                          Configure what audit logs to look for. This is only
                          used when "Custom condition" is selected.
                        </small>
                      </div>
                      <div className="nm-campaign-row">
                        <label>
                          <span>Service *</span>
                          <select
                            required={isCustomStop}
                            value={form.stopAuditMatch?.service || ""}
                            onChange={(e) =>
                              handleServiceChange(e.target.value)
                            }
                          >
                            <option value="">Select Service</option>
                            {services.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Action *</span>
                          <select
                            required={isCustomStop}
                            value={form.stopAuditMatch?.action || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                stopAuditMatch: {
                                  ...form.stopAuditMatch,
                                  action: e.target.value,
                                },
                              })
                            }
                            disabled={!form.stopAuditMatch?.service}
                          >
                            <option value="">
                              {form.stopAuditMatch?.service
                                ? "Select Action"
                                : "Select Service First"}
                            </option>
                            {actions.map((a) => (
                              <option key={a} value={a}>
                                {a}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Module (optional)</span>
                          <input
                            value={form.stopAuditMatch?.module || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                stopAuditMatch: {
                                  ...form.stopAuditMatch,
                                  module: e.target.value,
                                },
                              })
                            }
                            placeholder="e.g., child-management"
                          />
                        </label>
                      </div>
                      <div className="nm-campaign-row">
                        <label>
                          <span>Status</span>
                          <select
                            value={form.stopAuditMatch?.status || "SUCCESS"}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                stopAuditMatch: {
                                  ...form.stopAuditMatch,
                                  status: e.target.value as
                                    | "SUCCESS"
                                    | "FAILURE",
                                },
                              })
                            }
                          >
                            <option value="SUCCESS">Success</option>
                            <option value="FAILURE">Failure</option>
                          </select>
                        </label>
                        <label>
                          <span>Field Path to Check (optional)</span>
                          <input
                            value={form.stopAuditMatch?.fieldPath || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                stopAuditMatch: {
                                  ...form.stopAuditMatch,
                                  fieldPath: e.target.value,
                                },
                              })
                            }
                            placeholder="e.g., response.data.childId"
                          />
                        </label>
                        <label>
                          <span>Expected Value (optional)</span>
                          <input
                            value={form.stopAuditMatch?.expectedValue || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                stopAuditMatch: {
                                  ...form.stopAuditMatch,
                                  expectedValue: e.target.value,
                                },
                              })
                            }
                            placeholder="e.g., true"
                          />
                        </label>
                      </div>
                      <div className="nm-campaign-row">
                        <label>
                          <span>Field Operator</span>
                          <select
                            value={form.stopAuditMatch?.operator || "EQUALS"}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                stopAuditMatch: {
                                  ...form.stopAuditMatch,
                                  operator: e.target.value as
                                    | "EXISTS"
                                    | "NOT_EXISTS"
                                    | "EQUALS"
                                    | "NOT_EQUALS"
                                    | "GREATER_THAN"
                                    | "LESS_THAN"
                                    | "CONTAINS",
                                },
                              })
                            }
                          >
                            <option value="EXISTS">Exists</option>
                            <option value="NOT_EXISTS">Does not exist</option>
                            <option value="EQUALS">Equals</option>
                            <option value="NOT_EQUALS">Not equals</option>
                            <option value="GREATER_THAN">Greater than</option>
                            <option value="LESS_THAN">Less than</option>
                            <option value="CONTAINS">Contains</option>
                          </select>
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="nm-campaign-row">
                    <label>
                      <span>Total Days</span>
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={form.maxDays}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            maxDays: parseInt(e.target.value) || 5,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>Interval</span>
                      <select
                        value={form.interval}
                        onChange={(e) =>
                          setForm({ ...form, interval: e.target.value })
                        }
                      >
                        <option value="1m">1 minute (testing)</option>
                        <option value="5m">5 minutes (testing)</option>
                        <option value="1h">1 hour</option>
                        <option value="12h">Every 12 hours</option>
                        <option value="1d">Every day</option>
                        <option value="2d">Every 2 days</option>
                        <option value="7d">Every week</option>
                      </select>
                    </label>
                  </div>

                  <div className="nm-campaign-groups">
                    <label>
                      <span>Template Groups</span>
                      <small>
                        Each group needs templates for Email, Push, and In-App
                      </small>
                    </label>
                    {renderCampaignGroups()}
                    <button
                      type="button"
                      className="nm-secondary"
                      onClick={addTemplateGroup}
                    >
                      + Add Template Group
                    </button>
                  </div>

                  <div className="nm-campaign-row">
                    <label>
                      <span>Fallback Template (Optional)</span>
                      <select
                        value={form.fallbackTemplate}
                        onChange={(e) =>
                          setForm({ ...form, fallbackTemplate: e.target.value })
                        }
                      >
                        <option value="">No fallback</option>
                        {allTemplates.map((t) => (
                          <option
                            key={t._id || t.templateId}
                            value={t.templateId}
                          >
                            {t.templateId || t.name} ({t.channel})
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <label className="nm-campaign-enabled">
                    <input
                      type="checkbox"
                      checked={form.campaignEnabled}
                      onChange={(e) =>
                        setForm({ ...form, campaignEnabled: e.target.checked })
                      }
                    />
                    <span>Enable Campaign</span>
                  </label>
                </div>
              )}

              {!isCampaign && (
                <div className="nm-full nm-channel-templates">
                  <h4>Channel Templates</h4>
                  <p className="nm-hint">
                    Select which channels to use and assign a template
                  </p>
                  <div className="nm-channel-grid">
                    {(["EMAIL", "SMS", "PUSH", "IN_APP"] as Channel[]).map(
                      (ch) => (
                        <div className="nm-channel-item" key={ch}>
                          <label>
                            <input
                              type="checkbox"
                              checked={
                                form.channelTemplates[ch]?.enabled || false
                              }
                              onChange={(e) => {
                                const updated = { ...form.channelTemplates };
                                updated[ch] = {
                                  ...updated[ch],
                                  enabled: e.target.checked,
                                };
                                if (!e.target.checked) {
                                  updated[ch].templateId = "";
                                }
                                setForm({ ...form, channelTemplates: updated });
                              }}
                            />
                            <span>{ch}</span>
                          </label>
                          <select
                            disabled={!form.channelTemplates[ch]?.enabled}
                            value={form.channelTemplates[ch]?.templateId || ""}
                            onChange={(e) => {
                              const updated = { ...form.channelTemplates };
                              updated[ch] = {
                                ...updated[ch],
                                templateId: e.target.value,
                              };
                              setForm({ ...form, channelTemplates: updated });
                            }}
                          >
                            <option value="">Select {ch} template...</option>
                            {allTemplates
                              .filter(
                                (t) => t.channel === ch && t.isActive !== false,
                              )
                              .map((t) => (
                                <option
                                  key={t._id || t.templateId}
                                  value={t._id || t.templateId}
                                >
                                  {t.templateId || t.name} (v{t.version || 1})
                                </option>
                              ))}
                          </select>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              <button type="submit" className="nm-full">
                Save Rule
              </button>
            </form>
          )}
        </Modal>
      )}
    </>
  );
}

// ============================================================
// CAMPAIGNS PAGE
// ============================================================

function CampaignsPage() {
  const remote = useRemote(async () => {
    const [items, stats] = await Promise.all([
      notificationApi.campaigns.list(),
      notificationApi.campaigns.stats(),
    ]);
    return { items, stats };
  }, []);
  const [search, setSearch] = useState("");
  const rows = (remote.data?.items || []).filter((x) =>
    JSON.stringify(x).toLowerCase().includes(search.toLowerCase()),
  );
  const count = (key: string) =>
    remote.data?.stats.find((x) => x._id === key)?.count || 0;

  const viewDetails = (campaign: Campaign) => {
    const details: Record<string, any> = {
      "Campaign ID": campaign._id,
      "User ID": campaign.userId || "-",
      "Rule ID": campaign.ruleId || "-",
      Status: campaign.status,
      "Total Jobs": campaign.totalJobs || 0,
      "Stop Condition": campaign.stopCondition?.field || "-",
      "Stop Operator": campaign.stopCondition?.operator || "-",
      "Stop Value": campaign.stopCondition?.value || "-",
      // ✅ Show auditMatch if present
      ...(campaign.stopCondition?.auditMatch
        ? {
            "Audit Service": campaign.stopCondition.auditMatch.service || "-",
            "Audit Action": campaign.stopCondition.auditMatch.action || "-",
            "Audit Module": campaign.stopCondition.auditMatch.module || "-",
            "Audit Status": campaign.stopCondition.auditMatch.status || "-",
            "Field Path": campaign.stopCondition.auditMatch.fieldPath || "-",
            "Expected Value":
              campaign.stopCondition.auditMatch.expectedValue ?? "-",
            "Field Operator": campaign.stopCondition.auditMatch.operator || "-",
          }
        : {}),
      "Template Groups": campaign.templateGroups?.length || 0,
      Created: date(campaign.createdAt),
      Updated: date(campaign.updatedAt),
    };
    let msg = "📋 Campaign Details\n" + "=".repeat(40) + "\n";
    for (const [key, value] of Object.entries(details)) {
      msg += `${key}: ${value}\n`;
    }
    if (campaign.templateGroups?.length) {
      msg += "\n📝 Template Groups:\n";
      campaign.templateGroups.forEach((g, i) => {
        msg += `  ${i + 1}. ${g.name}: Days ${g.days.from}-${g.days.to} → ${g.templateId}\n`;
      });
    }
    alert(msg);
  };

  return (
    <>
      <Toolbar
        title="Campaigns"
        search={search}
        setSearch={setSearch}
        reload={remote.reload}
      />
      <PageState loading={remote.loading} error={remote.error}>
        <div className="nm-cards">
          {[
            ["Active", count("active")],
            ["Completed", count("completed")],
            ["Cancelled", count("cancelled")],
            ["Failed", count("failed")],
          ].map(([label, value]) => (
            <div className="nm-card" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        <div className="nm-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>User</th>
                <th>Rule</th>
                <th>Status</th>
                <th>Jobs</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x: Campaign) => (
                <tr key={x._id}>
                  <td>
                    <code>{x._id.slice(0, 12)}</code>
                  </td>
                  <td>{x.userId || "-"}</td>
                  <td>{x.ruleId || "-"}</td>
                  <td>
                    <Status value={x.status} />
                  </td>
                  <td>{x.totalJobs || 0}</td>
                  <td>{date(x.createdAt)}</td>
                  <td className="nm-actions">
                    <button onClick={() => viewDetails(x)}>View</button>
                    {x.status === "active" && (
                      <button
                        className="nm-danger"
                        onClick={async () => {
                          if (window.confirm("Cancel this campaign?")) {
                            await notificationApi.campaigns.cancel(x._id);
                            remote.reload();
                          }
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageState>
    </>
  );
}

// ============================================================
// LOG TABLE COMPONENT
// ============================================================

function LogTable({
  logs,
  reload,
}: {
  logs: NotificationLog[];
  reload?: () => void;
}) {
  const viewLog = (log: NotificationLog) => {
    const details = {
      ID: log._id,
      Event: log.businessAction || log.action || log.event || "-",
      Channel: log.channel || log.metadata?.channel || "-",
      Status: log.status || log.metadata?.status || "-",
      Recipient:
        log.recipient?.email || log.recipient?.userId || log.userId || "-",
      Created: date(log.createdAt || log.timestamp),
      "Correlation ID": log.correlationId || "-",
      "Provider Response": log.providerResponse || "-",
      Error: log.errorLog || log.errorDetails || "-",
    };
    let msg = "📋 Notification Details\n" + "=".repeat(40) + "\n";
    for (const [key, value] of Object.entries(details)) {
      msg += `${key}: ${typeof value === "object" ? JSON.stringify(value, null, 2) : value}\n`;
    }
    alert(msg);
  };

  return (
    <div className="nm-table-wrap">
      <table>
        <thead>
          <tr>
            <th>Time</th>
            <th>Event</th>
            <th>Recipient</th>
            <th>Channel</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((x) => (
            <tr key={x._id}>
              <td>{date(x.createdAt || x.timestamp)}</td>
              <td>
                {x.businessAction ||
                  x.action ||
                  x.event ||
                  x.metadata?.event ||
                  "-"}
              </td>
              <td>
                {x.recipient?.email ||
                  x.recipient?.userId ||
                  x.recipient?.name ||
                  x.actor?.email ||
                  x.actor?.name ||
                  x.userId ||
                  "-"}
              </td>
              <td>{x.channel || x.metadata?.channel || "-"}</td>
              <td>
                <Status value={x.status || x.metadata?.status || "UNKNOWN"} />
              </td>
              <td className="nm-actions">
                <button onClick={() => viewLog(x)}>View</button>
                {x.status === "FAILED" && reload && (
                  <button
                    className="nm-secondary"
                    onClick={async () => {
                      await notificationApi.logs.retry(x._id);
                      reload();
                    }}
                  >
                    Retry
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// LOGS PAGE
// ============================================================

function LogsPage() {
  const remote = useRemote(notificationApi.logs.list, []);
  const [search, setSearch] = useState("");
  const rows = (remote.data || []).filter((x) =>
    JSON.stringify(x).toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <>
      <Toolbar
        title="Notification Logs"
        search={search}
        setSearch={setSearch}
        reload={remote.reload}
      />
      <PageState
        loading={remote.loading}
        error={remote.error}
        empty={!rows.length}
      >
        <LogTable logs={rows} reload={remote.reload} />
      </PageState>
    </>
  );
}

// ============================================================
// QUEUE PAGE
// ============================================================

function QueuePage() {
  const remote = useRemote(notificationApi.queue, []);
  const raw = remote.data;
  const q: QueueMetrics =
    raw && "data" in raw ? (raw as { data: QueueMetrics }).data : raw || {};
  return (
    <>
      <Toolbar title="Queue Monitor" reload={remote.reload} />
      <PageState loading={remote.loading} error={remote.error}>
        <div className="nm-cards">
          {[
            ["Waiting", q.waiting || 0],
            ["Active", q.active || 0],
            ["Completed", q.completed || 0],
            ["Failed", q.failed || 0],
          ].map(([k, v]) => (
            <div className="nm-card" key={k}>
              <span>{k}</span>
              <strong>{v}</strong>
            </div>
          ))}
        </div>
      </PageState>
    </>
  );
}

// ============================================================
// SETTINGS PAGE
// ============================================================

function SettingsPage() {
  return (
    <>
      <Toolbar title="Settings" reload={() => undefined} />
      <div className="nm-panel">
        <h3>API configuration</h3>
        <p>
          Set <code>REACT_APP_NOTIFICATION_API_URL</code> and{" "}
          <code>REACT_APP_AUDIT_API_URL</code> in the project <code>.env</code>.
          Authentication continues through the shared Axios interceptor.
        </p>
      </div>
    </>
  );
}

// ============================================================
// MODAL COMPONENT
// ============================================================

function Modal({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="nm-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <section className="nm-modal" role="dialog" aria-modal="true">
        <header>
          <h2>{title}</h2>
          <button className="nm-icon" onClick={close} aria-label="Close">
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

// ============================================================
// STATUS COMPONENT
// ============================================================

function Status({ value }: { value: string }) {
  return (
    <span
      className={`nm-status nm-status-${value.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      {value}
    </span>
  );
}

// ============================================================
// MAIN EXPORT
// ============================================================

export default function NotificationManagement() {
  const [page, setPage] = useState<PageKey>("dashboard");
  const view = useMemo(() => {
    switch (page) {
      case "dashboard":
        return <Dashboard />;
      case "campaigns":
        return <CampaignsPage />;
      case "events":
        return <EventsPage />;
      case "templates":
        return <TemplatesPage />;
      case "rules":
        return <RulesPage />;
      case "logs":
        return <LogsPage />;
      case "queues":
        return <QueuePage />;
      case "settings":
        return <SettingsPage />;
      default:
        return <SettingsPage />;
    }
  }, [page]);

  return (
    <div className="nm-shell">
      <nav className="admin-center-topbar">
        {menu.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`admin-center-tab ${page === key ? "active" : ""}`}
            onClick={() => setPage(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      <main className="nm-content">{view}</main>
    </div>
  );
}
